"use client";

import { useChainInfoMap } from "@/app/(v2)/workflows/_hooks/useChainInfo";
import { isValidAddressForChain, isValidSolanaAddress, useAddressValidation } from "@/hooks/useAddressValidation";
import { useB3Profile } from "@/hooks/useB3Profile";
import { useSimBalance, type SimBalanceItem } from "@/hooks/useSimTokenInfo";
import { filterWalletsByChain } from "@/lib/utils/chain-family";
import { useWalletsStore } from "@/stores/walletsStore";
import type { OrganizationWallet } from "@/types/wallet";
import { memo, useEffect, useMemo, useRef, useState } from "react";
import { AddressInputView, OrganizationWalletOptionView, type AddressInputMode, type WalletBalanceSummary } from "./AddressInputView";
interface AddressInputProps {
  label: string;
  description?: string;
  headerRight?: React.ReactNode;
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  required?: boolean;
  /** Filter wallet picker to wallets compatible with this chain */
  chainId?: number;
  /** When true, Popover uses modal mode (required inside dialogs to receive pointer events) */
  popoverModal?: boolean;
  /**
   * Force all variants (Treasury-wallet card / ENS-profile card / address-only
   * card / plain input) to share the shorter `min-h-[42px]` height. Used by
   * grid call sites (csvTable) where stacked rows must not jitter between
   * 56px and 42px as a row's address toggles between "matches a known wallet"
   * and "plain hex". Hides the ambient balance/chains meta on the Treasury
   * variant — space doesn't fit, and the meta is redundant inside a grid row.
   */
  compact?: boolean;
  /**
   * When false, hides the "Wallets" badge and the org-wallet autocomplete
   * popover. Use for fields that take a third-party address (e.g. a deployer
   * filter) where suggesting the user's own org wallets is misleading.
   * Default true preserves existing behavior.
   */
  allowWalletPicker?: boolean;
}
function formatUsd(totalUsd: number) {
  if (totalUsd >= 1000000) {
    return `$${(totalUsd / 1000000).toFixed(2)}M`;
  }
  if (totalUsd >= 1000) {
    return `$${(totalUsd / 1000).toFixed(2)}K`;
  }
  if (totalUsd >= 1) {
    return `$${totalUsd.toFixed(2)}`;
  }
  if (totalUsd > 0) {
    return `$${totalUsd.toFixed(4)}`;
  }
  return null;
}

/**
 * Connected variant of `OrganizationWalletOptionView`. Keeps the per-wallet
 * `useSimBalance` + `useChainInfoMap` calls co-located with the row that
 * paints them (one hook order per wallet) and forwards the result to the
 * pure view.
 */
function OrganizationWalletOption({
  wallet,
  className,
  nameClassName
}: {
  wallet: OrganizationWallet;
  className?: string;
  nameClassName?: string;
}) {
  const {
    data: balanceData,
    isLoading: isLoadingBalance
  } = useSimBalance(wallet.address);
  const {
    totalUsd,
    chainsWithBalances
  } = useMemo(() => {
    if (!balanceData?.balances) return {
      totalUsd: 0,
      chainsWithBalances: [] as number[]
    };
    let total = 0;
    const chainSet = new Set<number>();
    balanceData.balances.forEach((balance: SimBalanceItem) => {
      if (balance.value_usd && balance.value_usd > 0) {
        total += balance.value_usd;
        chainSet.add(balance.chain_id);
      }
    });
    return {
      totalUsd: total,
      chainsWithBalances: Array.from(chainSet)
    };
  }, [balanceData]);
  const topChains = chainsWithBalances.slice(0, 3);
  const {
    chainInfoMap,
    isLoading: isLoadingChains
  } = useChainInfoMap(topChains);
  const summary: WalletBalanceSummary = useMemo(() => ({
    usdValue: formatUsd(totalUsd),
    chains: topChains.map(chainId => {
      const chainInfo = chainInfoMap.get(chainId);
      return {
        id: chainId,
        name: chainInfo?.name || `Chain ${chainId}`,
        iconUrl: chainInfo?.iconUrl
      };
    }),
    extraChainsCount: chainsWithBalances.length > 3 ? chainsWithBalances.length - 3 : 0,
    isLoadingBalance,
    isLoadingChains
  }), [totalUsd, topChains, chainInfoMap, chainsWithBalances.length, isLoadingBalance, isLoadingChains]);
  return <OrganizationWalletOptionView wallet={wallet} summary={summary} className={className} nameClassName={nameClassName} />;
}
function AddressInput({
  label,
  description,
  headerRight,
  value,
  onChange,
  placeholder = "Enter a blockchain address",
  required = false,
  chainId,
  popoverModal = false,
  compact = false,
  allowWalletPicker = true
}: AddressInputProps) {
  const [isTouched, setTouched] = useState(false);
  const [open, setOpen] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const {
    wallets: orgWallets,
    loadWallets
  } = useWalletsStore();
  const safeValue = typeof value === "string" ? value : value != null ? String(value) : "";

  // Check if current value is a valid address or ENS name
  // Use chain-aware validation: on Solana chains, reject 0x addresses; on EVM chains, reject base58
  const isValidAddress = !!safeValue && isValidAddressForChain(safeValue, chainId) && !safeValue.includes(".");
  const isENSName = !!(safeValue && !safeValue.startsWith("0x") && safeValue.includes("."));

  // Use address validation for 0x addresses
  const {
    showError: showAddressError
  } = useAddressValidation(safeValue, chainId);

  // Fetch profile data when we have a potential ENS name (non-0x input with at least 3 chars) or a valid EVM address
  // Skip profile fetch for Solana addresses (no ENS equivalent)
  const isSolana = !!(safeValue && isValidSolanaAddress(safeValue));
  const shouldFetchProfile = !!(safeValue && !safeValue.startsWith("0x") && safeValue.length >= 3 && !isSolana);
  const {
    data: profile,
    isLoading: isLoadingProfile
  } = useB3Profile({
    addressOrName: safeValue,
    enabled: Boolean(shouldFetchProfile || isValidAddress || isENSName)
  });
  const prevProfileRef = useRef<typeof profile>(undefined);
  useEffect(() => {
    loadWallets();
  }, [loadWallets]);
  const normalizedValue = safeValue.trim().toLowerCase();
  const selectedWallet = useMemo(() => orgWallets.find(wallet => wallet.address.toLowerCase() === normalizedValue) || null, [orgWallets, normalizedValue]);
  const chainFilteredWallets = useMemo(() => filterWalletsByChain(orgWallets, chainId), [orgWallets, chainId]);
  const matchingWallets = useMemo(() => {
    if (!allowWalletPicker) return [];
    if (!normalizedValue) return chainFilteredWallets;
    return chainFilteredWallets.filter(wallet_0 => {
      const walletAddress = wallet_0.address.toLowerCase();
      const walletName = wallet_0.name.toLowerCase();
      return walletAddress.includes(normalizedValue) || walletName.includes(normalizedValue);
    });
  }, [allowWalletPicker, chainFilteredWallets, normalizedValue]);

  // Resolved address (from profile or direct input)
  const resolvedAddress = profile?.address || (isValidAddress ? safeValue : null);

  // Determine if we have a valid selection to show the profile card
  const hasValidSelection = (isValidAddress || isENSName && profile) && !isEditing;

  // Compute presentation mode for the view
  const mode: AddressInputMode = !hasValidSelection ? "input" : selectedWallet ? "wallet" : profile ? "profile" : isValidAddress ? "address-only" : "input";
  const showError = isTouched && !safeValue && required;

  // Open popover when profile data arrives for ENS lookup while editing
  useEffect(() => {
    // Only open if profile just loaded (transition from undefined to defined)
    if (isEditing && shouldFetchProfile && profile && !isValidAddress) {
      if (!prevProfileRef.current) {
        queueMicrotask(() => setOpen(true));
      }
    }
    prevProfileRef.current = profile;
  }, [profile, isEditing, shouldFetchProfile, isValidAddress]);
  const handleInputChange = (newValue: string) => {
    onChange(newValue);

    // Close popover if input is cleared or is a valid address (EVM or Solana)
    if (!newValue || newValue.startsWith("0x") && newValue.length === 42 || isValidSolanaAddress(newValue)) {
      setOpen(false);
    } else if (matchingWallets.length > 0) {
      // Open popover when typing and there are matches
      setOpen(true);
    }
  };
  const handleSelectWallet = (address: string) => {
    onChange(address);
    setOpen(false);
    setIsEditing(false);
  };
  const handleSelectProfile = () => {
    if (profile?.address) {
      onChange(profile.address);
      setOpen(false);
      setIsEditing(false);
    }
  };
  const handleEditClick = () => {
    setIsEditing(true);
    // Show popover if we have a profile result for ENS input
    if (shouldFetchProfile && profile) {
      setTimeout(() => setOpen(true), 0);
    }
  };
  const handleFocus = () => {
    // Show popover if we have wallet matches or a profile result for current input
    if (matchingWallets.length > 0 || shouldFetchProfile && profile) {
      setOpen(true);
    }
  };
  const handleBlur = () => {
    setTouched(true);
    // Delay closing to allow click on popover item
    setTimeout(() => {
      setOpen(false);
      setIsEditing(false);
    }, 200);
  };
  const handleWalletsButtonClick = () => {
    setIsEditing(true);
    setOpen(true);
    inputRef.current?.focus();
  };
  return <AddressInputView ref={inputRef} label={label} description={description} headerRight={headerRight} value={safeValue} onChange={handleInputChange} placeholder={placeholder} required={required} popoverModal={popoverModal} compact={compact} allowWalletPicker={allowWalletPicker} mode={mode} isEditing={isEditing} open={open} onOpenChange={setOpen} onEditClick={handleEditClick} onFocus={handleFocus} onBlur={handleBlur} onWalletClick={handleSelectWallet} onProfileClick={handleSelectProfile} onWalletsButtonClick={handleWalletsButtonClick} selectedWallet={selectedWallet} profile={profile ?? null} resolvedAddress={resolvedAddress} matchingWallets={matchingWallets} renderWalletBody={(wallet_1, opts) => <OrganizationWalletOption wallet={wallet_1} className="min-w-0 flex-1" nameClassName={opts.variant === "match" ? "body-sm-medium truncate text-ds-text-primary" : undefined} />} isLoadingProfile={isLoadingProfile} isValidAddress={isValidAddress} isENSName={isENSName} showError={showError} showAddressError={showAddressError} />;
}
export default memo(AddressInput);